import { alpha, createTheme, lighten, darken } from '@mui/material';
import '@mui/lab/themeAugmentation';
import { brand } from '../brand';

const themeColors = {
  primary: brand.green,
  secondary: brand.muted,
  accent: brand.lime,
  success: brand.success,
  warning: brand.warning,
  error: brand.error,
  info: brand.info,
  black: brand.ink,
  white: brand.white,
  primaryAlt: brand.slate,
  sidebarBg: brand.surface
};

const colors = {
  gradients: {
    blue1: brand.gradient,
    blue2: 'linear-gradient(135deg, #C8E86A 0%, #1B9E2E 100%)',
    blue3: brand.slate,
    blue4: brand.green,
    blue5: 'linear-gradient(135deg, #A4D007 10%, #0F7A1C 100%)',
    orange1: 'linear-gradient(135deg, #FCCF31 0%, #F55555 100%)',
    orange2: 'linear-gradient(135deg, #FFD3A5 0%, #FD6585 100%)',
    orange3: 'linear-gradient(120deg, #f6d365 0%, #fda085 100%)',
    purple1: brand.green,
    purple3: brand.greenDark,
    pink1: brand.lime,
    pink2: brand.gradient,
    green1: 'linear-gradient(135deg, #FFF720 0%, #3CD500 100%)',
    green2: 'linear-gradient(to bottom, #00b09b, #96c93d)',
    black1: 'linear-gradient(100.66deg, #434343 6.56%, #000000 93.57%)',
    black2: 'linear-gradient(60deg, #29323c 0%, #485563 100%)'
  },
  shadows: {
    success:
      '0px 1px 4px rgba(15, 159, 110, 0.2), 0px 3px 12px 2px rgba(15, 159, 110, 0.12)',
    error:
      '0px 1px 4px rgba(220, 38, 38, 0.2), 0px 3px 12px 2px rgba(220, 38, 38, 0.12)',
    info: '0px 1px 4px rgba(2, 132, 199, 0.2), 0px 3px 12px 2px rgba(2, 132, 199, 0.12)',
    primary:
      '0px 1px 3px rgba(27, 158, 46, 0.18), 0px 4px 12px rgba(27, 158, 46, 0.12)',
    warning:
      '0px 1px 4px rgba(217, 119, 6, 0.2), 0px 3px 12px 2px rgba(217, 119, 6, 0.12)',
    card: '0 1px 2px rgba(15, 23, 42, 0.04)',
    cardSm: '0 1px 2px rgba(15, 23, 42, 0.03)',
    cardLg: '0 8px 24px rgba(15, 23, 42, 0.08)'
  },
  layout: {
    general: {
      bodyBg: brand.canvas
    },
    sidebar: {
      background: themeColors.sidebarBg,
      textColor: brand.muted,
      dividerBg: brand.border,
      menuItemColor: brand.muted,
      menuItemColorActive: brand.ink,
      menuItemBg: 'transparent',
      menuItemBgActive: brand.subtle,
      menuItemIconColor: brand.muted,
      menuItemIconColorActive: brand.ink,
      menuItemHeadingColor: alpha(brand.ink, 0.4)
    }
  },
  alpha: {
    white: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white
    },
    trueWhite: {
      5: alpha(themeColors.white, 0.02),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white
    },
    black: {
      5: alpha(themeColors.black, 0.02),
      10: alpha(themeColors.black, 0.1),
      30: alpha(themeColors.black, 0.3),
      50: alpha(themeColors.black, 0.5),
      70: alpha(themeColors.black, 0.7),
      100: themeColors.black
    }
  },
  secondary: {
    lighter: lighten(themeColors.secondary, 0.85),
    light: lighten(themeColors.secondary, 0.25),
    main: themeColors.secondary,
    dark: darken(themeColors.secondary, 0.2)
  },
  primary: {
    lighter: lighten(themeColors.primary, 0.85),
    light: lighten(themeColors.primary, 0.3),
    main: themeColors.primary,
    dark: darken(themeColors.primary, 0.2)
  },
  success: {
    lighter: lighten(themeColors.success, 0.85),
    light: lighten(themeColors.success, 0.3),
    main: themeColors.success,
    dark: darken(themeColors.success, 0.2)
  },
  warning: {
    lighter: lighten(themeColors.warning, 0.85),
    light: lighten(themeColors.warning, 0.3),
    main: themeColors.warning,
    dark: darken(themeColors.warning, 0.2)
  },
  error: {
    lighter: lighten(themeColors.error, 0.85),
    light: lighten(themeColors.error, 0.3),
    main: themeColors.error,
    dark: darken(themeColors.error, 0.2)
  },
  info: {
    lighter: lighten(themeColors.info, 0.85),
    light: lighten(themeColors.info, 0.3),
    main: themeColors.info,
    dark: darken(themeColors.info, 0.2)
  }
};

export const PureLightTheme = createTheme({
  // direction: i18n.dir(),
  colors: {
    gradients: {
      blue1: colors.gradients.blue1,
      blue2: colors.gradients.blue2,
      blue3: colors.gradients.blue3,
      blue4: colors.gradients.blue4,
      blue5: colors.gradients.blue5,
      orange1: colors.gradients.orange1,
      orange2: colors.gradients.orange2,
      orange3: colors.gradients.orange3,
      purple1: colors.gradients.purple1,
      purple3: colors.gradients.purple3,
      pink1: colors.gradients.pink1,
      pink2: colors.gradients.pink2,
      green1: colors.gradients.green1,
      green2: colors.gradients.green2,
      black1: colors.gradients.black1,
      black2: colors.gradients.black2
    },
    shadows: {
      success: colors.shadows.success,
      error: colors.shadows.error,
      primary: colors.shadows.primary,
      info: colors.shadows.info,
      warning: colors.shadows.warning
    },
    alpha: {
      white: {
        5: alpha(themeColors.white, 0.02),
        10: alpha(themeColors.white, 0.1),
        30: alpha(themeColors.white, 0.3),
        50: alpha(themeColors.white, 0.5),
        70: alpha(themeColors.white, 0.7),
        100: themeColors.white
      },
      trueWhite: {
        5: alpha(themeColors.white, 0.02),
        10: alpha(themeColors.white, 0.1),
        30: alpha(themeColors.white, 0.3),
        50: alpha(themeColors.white, 0.5),
        70: alpha(themeColors.white, 0.7),
        100: themeColors.white
      },
      black: {
        5: alpha(themeColors.black, 0.02),
        10: alpha(themeColors.black, 0.1),
        30: alpha(themeColors.black, 0.3),
        50: alpha(themeColors.black, 0.5),
        70: alpha(themeColors.black, 0.7),
        100: themeColors.black
      }
    },
    secondary: {
      lighter: alpha(themeColors.secondary, 0.1),
      light: lighten(themeColors.secondary, 0.3),
      main: themeColors.secondary,
      dark: darken(themeColors.secondary, 0.2)
    },
    primary: {
      lighter: alpha(themeColors.primary, 0.1),
      light: lighten(themeColors.primary, 0.3),
      main: themeColors.primary,
      dark: darken(themeColors.primary, 0.2)
    },
    success: {
      lighter: alpha(themeColors.success, 0.1),
      light: lighten(themeColors.success, 0.3),
      main: themeColors.success,
      dark: darken(themeColors.success, 0.2)
    },
    warning: {
      lighter: alpha(themeColors.warning, 0.1),
      light: lighten(themeColors.warning, 0.3),
      main: themeColors.warning,
      dark: darken(themeColors.warning, 0.2)
    },
    error: {
      lighter: alpha(themeColors.error, 0.1),
      light: lighten(themeColors.error, 0.3),
      main: themeColors.error,
      dark: darken(themeColors.error, 0.2)
    },
    info: {
      lighter: alpha(themeColors.info, 0.1),
      light: lighten(themeColors.info, 0.3),
      main: themeColors.info,
      dark: darken(themeColors.info, 0.2)
    }
  },
  general: {
    reactFrameworkColor: brand.green,
    borderRadiusSm: '6px',
    borderRadius: '10px',
    borderRadiusLg: '12px',
    borderRadiusXl: '16px'
  },
  sidebar: {
    background: colors.layout.sidebar.background,
    textColor: colors.layout.sidebar.textColor,
    dividerBg: colors.layout.sidebar.dividerBg,
    menuItemColor: colors.layout.sidebar.menuItemColor,
    menuItemColorActive: colors.layout.sidebar.menuItemColorActive,
    menuItemBg: colors.layout.sidebar.menuItemBg,
    menuItemBgActive: colors.layout.sidebar.menuItemBgActive,
    menuItemIconColor: colors.layout.sidebar.menuItemIconColor,
    menuItemIconColorActive: colors.layout.sidebar.menuItemIconColorActive,
    menuItemHeadingColor: colors.layout.sidebar.menuItemHeadingColor,
    boxShadow: 'none',
    width: '248px'
  },
  header: {
    height: '64px',
    background: colors.alpha.white[100],
    boxShadow: 'none',
    textColor: colors.secondary.main
  },
  spacing: 8,
  palette: {
    common: {
      black: colors.alpha.black[100],
      white: colors.alpha.white[100]
    },
    mode: 'light',
    primary: {
      light: colors.primary.light,
      main: colors.primary.main,
      dark: colors.primary.dark
    },
    secondary: {
      light: colors.secondary.light,
      main: colors.secondary.main,
      dark: colors.secondary.dark
    },
    error: {
      light: colors.error.light,
      main: colors.error.main,
      dark: colors.error.dark,
      contrastText: colors.alpha.white[100]
    },
    success: {
      light: colors.success.light,
      main: colors.success.main,
      dark: colors.success.dark,
      contrastText: colors.alpha.white[100]
    },
    info: {
      light: colors.info.light,
      main: colors.info.main,
      dark: colors.info.dark,
      contrastText: colors.alpha.white[100]
    },
    warning: {
      light: colors.warning.light,
      main: colors.warning.main,
      dark: colors.warning.dark,
      contrastText: colors.alpha.white[100]
    },
    text: {
      primary: colors.alpha.black[100],
      secondary: colors.alpha.black[70],
      disabled: colors.alpha.black[50]
    },
    background: {
      paper: colors.alpha.white[100],
      default: colors.layout.general.bodyBg
    },
    action: {
      active: colors.alpha.black[100],
      hover: colors.primary.lighter,
      hoverOpacity: 0.1,
      selected: colors.alpha.black[10],
      selectedOpacity: 0.1,
      disabled: colors.alpha.black[50],
      disabledBackground: colors.alpha.black[5],
      disabledOpacity: 0.38,
      focus: colors.alpha.black[10],
      focusOpacity: 0.05,
      activatedOpacity: 0.12
    },
    tonalOffset: 0.5
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1840
    }
  },
  components: {
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(darken(themeColors.primaryAlt, 0.4), 0.2),
          backdropFilter: 'blur(2px)',

          '&.MuiBackdrop-invisible': {
            backgroundColor: 'transparent',
            backdropFilter: 'blur(2px)'
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          marginLeft: 8,
          marginRight: 8,
          fontWeight: 'bold'
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        'html, body': {
          width: '100%',
          height: '100%'
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          flex: 1
        },
        '#root': {
          width: '100%',
          height: '100%',
          display: 'flex',
          flex: 1,
          flexDirection: 'column'
        },
        html: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased'
        },
        '.child-popover .MuiPaper-root .MuiList-root': {
          flexDirection: 'column'
        },
        '#nprogress': {
          pointerEvents: 'none'
        },
        '#nprogress .bar': {
          background: colors.primary.lighter
        },
        '#nprogress .spinner-icon': {
          borderTopColor: colors.primary.lighter,
          borderLeftColor: colors.primary.lighter
        },
        '#nprogress .peg': {
          boxShadow:
            '0 0 15px ' +
            colors.primary.lighter +
            ', 0 0 8px' +
            colors.primary.light
        },
        ':root': {
          '--swiper-theme-color': colors.primary.main
        },
        '::-webkit-scrollbar': {
          width: 8,
          height: 8
        },
        '::-webkit-scrollbar-thumb': {
          background: alpha(themeColors.secondary, 0.35),
          borderRadius: 8
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: alpha(themeColors.primary, 0.45)
        },
        '::-webkit-scrollbar-track': {
          background: 'transparent'
        },
        /* Card titles on list/table screens: section-sized, below the page heading */
        '.MuiCard-root .MuiTypography-h4': {
          fontSize: '0.9375rem !important',
          fontWeight: '700 !important',
          letterSpacing: '-0.01em !important',
          lineHeight: '1.3 !important'
        },
        /* Content density + polish for data tables */
        '.MuiTableContainer-root': {
          background: colors.alpha.white[100],
          borderRadius: 0
        },
        '.MuiTablePagination-root': {
          borderTop: '1px solid ' + alpha(themeColors.black, 0.06),
          paddingLeft: 12,
          paddingRight: 12,
          fontSize: '0.8125rem',
          color: colors.alpha.black[70]
        },
        code: {
          background: colors.info.lighter,
          color: colors.info.dark,
          borderRadius: 4,
          padding: 4
        },
        '@keyframes ripple': {
          '0%': {
            transform: 'scale(.8)',
            opacity: 1
          },
          '100%': {
            transform: 'scale(2.8)',
            opacity: 0
          }
        },
        '@keyframes float': {
          '0%': {
            transform: 'translate(0%, 0%)'
          },
          '100%': {
            transform: 'translate(3%, 3%)'
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        iconOutlined: {
          color: colors.alpha.black[50]
        },
        icon: {
          top: 'calc(50% - 14px)'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: colors.alpha.white[100],
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(themeColors.black, 0.12)
          },
          '& .MuiInputAdornment-positionEnd.MuiInputAdornment-outlined': {
            paddingRight: 6
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(themeColors.black, 0.3)
          },
          '&.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main
          }
        }
      }
    },
    MuiListSubheader: {
      styleOverrides: {
        colorPrimary: {
          fontWeight: 'bold',
          lineHeight: '40px',
          fontSize: 13,
          background: colors.alpha.black[5],
          color: colors.alpha.black[70]
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '16px 20px',
          minHeight: 60,
          // A Typography passed as `title` is rendered unwrapped; keep it card-sized too.
          '& .MuiCardHeader-content > .MuiTypography-root:first-of-type': {
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1.3
          },
          background: brand.gradientCardHeader,
          borderBottom: '1px solid ' + alpha(themeColors.black, 0.06),
          flexWrap: 'wrap',
          gap: 12,
          '@media (max-width: 900px)': {
            flexDirection: 'column',
            alignItems: 'stretch',
            '& .MuiCardHeader-action': {
              margin: 0,
              width: '100%',
              alignSelf: 'stretch'
            }
          }
        },
        action: {
          marginTop: 0,
          marginBottom: 0,
          alignSelf: 'center'
        },
        title: {
          fontSize: 15,
          // Pages often pass their own heading Typography as the title; keep it card-sized.
          '& .MuiTypography-root': {
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1.3
          },
          fontWeight: 700,
          letterSpacing: '-0.02em',
          lineHeight: 1.3
        },
        subheader: {
          fontSize: 12.5,
          marginTop: 2,
          color: colors.alpha.black[70]
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          border: 'none',
          boxShadow: '0 24px 60px rgba(22, 25, 22, 0.14)'
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          letterSpacing: '-0.02em',
          padding: '20px 24px',
          background: brand.gradientCardHeader,
          borderBottom: '1px solid ' + alpha(themeColors.black, 0.06)
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '24px'
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          borderTop: '1px solid ' + alpha(themeColors.black, 0.06)
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          borderRadius: '50px'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600
        },
        outlined: {
          backgroundColor: colors.alpha.white[100],
          borderColor: alpha(themeColors.black, 0.12),
          '&.MuiChip-colorPrimary': {
            backgroundColor: alpha(brand.chartLime, 0.14),
            borderColor: alpha(brand.green, 0.28),
            color: brand.greenDark
          }
        },
        colorPrimary: {
          backgroundColor: colors.primary.main,
          backgroundImage: 'none',
          color: colors.alpha.white[100]
        },
        colorSecondary: {
          background: colors.alpha.black[5],
          color: colors.alpha.black[100],

          '&:hover': {
            background: colors.alpha.black[10]
          }
        },
        deleteIcon: {
          color: colors.error.light,

          '&:hover': {
            color: colors.error.main
          }
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',

          '&.Mui-expanded': {
            margin: 0
          },
          '&::before': {
            display: 'none'
          }
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 'bold'
        },
        colorDefault: {
          background: colors.alpha.black[30],
          color: colors.alpha.white[100]
        }
      }
    },
    MuiAvatarGroup: {
      styleOverrides: {
        root: {
          alignItems: 'center'
        },
        avatar: {
          background: colors.alpha.black[10],
          fontSize: 13,
          color: colors.alpha.black[70],
          fontWeight: 'bold',

          '&:first-of-type': {
            border: 0,
            background: 'transparent'
          }
        }
      }
    },
    MuiListItemAvatar: {
      styleOverrides: {
        alignItemsFlexStart: {
          marginTop: 0
        }
      }
    },
    MuiPaginationItem: {
      styleOverrides: {
        page: {
          fontSize: 13,
          fontWeight: 'bold',
          transition: 'all .2s'
        },
        textPrimary: {
          '&.Mui-selected': {
            boxShadow: colors.shadows.primary
          },
          '&.MuiButtonBase-root:hover': {
            background: colors.alpha.black[5]
          },
          '&.Mui-selected.MuiButtonBase-root:hover': {
            background: colors.primary.main
          }
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: {
          fontWeight: 600,
          textTransform: 'none',
          paddingLeft: 14,
          paddingRight: 14,
          borderRadius: 10,
          letterSpacing: '-0.01em',
          whiteSpace: 'nowrap',

          '.MuiSvgIcon-root': {
            transition: 'all .2s'
          }
        },
        endIcon: {
          marginRight: -8
        },
        containedPrimary: {
          backgroundColor: brand.action,
          backgroundImage: 'none',
          color: colors.alpha.white[100],
          boxShadow: 'none',

          '&:hover': {
            backgroundColor: brand.actionHover,
            backgroundImage: 'none',
            boxShadow: 'none'
          }
        },
        outlinedPrimary: {
          backgroundColor: colors.alpha.white[100],
          color: brand.ink,
          borderColor: alpha(themeColors.black, 0.12),

          '&:hover': {
            backgroundColor: brand.subtle,
            borderColor: alpha(themeColors.black, 0.22)
          }
        },
        textPrimary: {
          color: brand.ink,

          '&:hover': {
            backgroundColor: brand.subtle
          }
        },
        containedSecondary: {
          backgroundColor: colors.secondary.main,
          color: colors.alpha.white[100],
          border: '1px solid ' + colors.alpha.black[30]
        },
        outlinedSecondary: {
          backgroundColor: colors.alpha.white[100],

          '&:hover, &.MuiSelected': {
            backgroundColor: colors.alpha.black[5],
            color: colors.alpha.black[100]
          }
        },
        sizeSmall: {
          padding: '4px 12px',
          lineHeight: 1.5,
          fontSize: 13
        },
        sizeMedium: {
          padding: '7px 16px',
          fontSize: 13.5
        },
        sizeLarge: {
          padding: '10px 22px'
        },
        textSizeSmall: {
          padding: '4px 10px'
        },
        textSizeMedium: {
          padding: '7px 12px'
        },
        textSizeLarge: {
          padding: '12px 16px'
        }
      }
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false
      },
      styleOverrides: {
        root: {
          borderRadius: 6
        }
      }
    },
    MuiToggleButton: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: {
          color: colors.primary.main,
          background: colors.alpha.white[100],
          transition: 'all .2s',

          '&:hover, &.Mui-selected, &.Mui-selected:hover': {
            color: colors.alpha.white[100],
            background: colors.primary.main
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: 8,

          '& .MuiTouchRipple-root': {
            borderRadius: 8
          }
        },
        sizeSmall: {
          padding: 4
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: 0
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '& .MuiTouchRipple-root': {
            opacity: 0.3
          }
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          background: colors.alpha.black[10],
          border: 0,
          height: 1
        },
        vertical: {
          height: 'auto',
          width: 1,

          '&.MuiDivider-flexItem.MuiDivider-fullWidth': {
            height: 'auto'
          },
          '&.MuiDivider-absolute.MuiDivider-fullWidth': {
            height: '100%'
          }
        },
        withChildren: {
          '&:before, &:after': {
            border: 0
          }
        },
        wrapper: {
          background: colors.alpha.white[100],
          fontWeight: 'bold',
          height: 24,
          lineHeight: '24px',
          marginTop: -12,
          color: 'inherit',
          textTransform: 'uppercase'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          padding: 0,
          backgroundImage: 'none'
        },
        elevation0: {
          boxShadow: 'none'
        },
        elevation: {
          boxShadow: brand.cardShadow,
          border: 'none'
        },
        elevation1: {
          boxShadow: brand.cardShadow,
          border: 'none'
        },
        elevation2: {
          boxShadow: colors.shadows.cardSm,
          border: '1px solid ' + alpha(themeColors.black, 0.05)
        },
        elevation24: {
          boxShadow: colors.shadows.cardLg
        },
        outlined: {
          boxShadow: colors.shadows.cardSm,
          border: '1px solid ' + alpha(themeColors.black, 0.08)
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          '&:last-child': { paddingBottom: 20 }
        }
      }
    },
    MuiTextField: {
      defaultProps: { size: 'small' }
    },
    MuiFormControl: {
      defaultProps: { size: 'small' }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: brand.radius,
          boxShadow: brand.cardShadow,
          border: 'none',
          overflow: 'hidden'
        }
      }
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover'
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          height: 6
        }
      }
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '& .MuiSlider-valueLabelCircle, .MuiSlider-valueLabelLabel': {
            transform: 'none'
          },
          '& .MuiSlider-valueLabel': {
            borderRadius: 6,
            background: colors.alpha.black[100],
            color: colors.alpha.white[100]
          }
        }
      }
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,

          '& .MuiListItem-button': {
            transition: 'all .2s',

            '& > .MuiSvgIcon-root': {
              minWidth: 34
            },

            '& .MuiTouchRipple-root': {
              opacity: 0.2
            }
          },
          '& .MuiListItem-root.MuiButtonBase-root.Mui-selected': {
            backgroundColor: alpha(colors.primary.lighter, 0.4)
          },
          '& .MuiMenuItem-root.MuiButtonBase-root:active': {
            backgroundColor: alpha(colors.primary.lighter, 0.4)
          },
          '& .MuiMenuItem-root.MuiButtonBase-root .MuiTouchRipple-root': {
            opacity: 0.2
          }
        },
        padding: {
          padding: '12px',

          '& .MuiListItem-button': {
            borderRadius: 6,
            margin: '1px 0'
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40
        },
        indicator: {
          height: 2,
          borderRadius: '2px 2px 0 0',
          backgroundColor: colors.primary.main
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          minWidth: 'auto',
          padding: '0 14px',
          fontSize: 13.5,
          fontWeight: 600,
          textTransform: 'none',
          color: colors.alpha.black[50],
          transition: 'color .15s',
          '&:hover': {
            color: colors.alpha.black[100]
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            color: colors.primary.main
          }
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          padding: 12
        },
        list: {
          padding: 12,

          '& .MuiMenuItem-root.MuiButtonBase-root': {
            fontSize: 14,
            marginTop: 1,
            marginBottom: 1,
            transition: 'all .2s',
            color: colors.alpha.black[70],

            '& .MuiTouchRipple-root': {
              opacity: 0.2
            },

            '&:hover, &:active, &.active, &.Mui-selected': {
              color: colors.alpha.black[100],
              background: alpha(colors.primary.lighter, 0.4)
            }
          }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          background: 'transparent',
          transition: 'all .2s',

          '&:hover, &:active, &.active, &.Mui-selected': {
            color: colors.alpha.black[100],
            background: alpha(colors.primary.lighter, 0.4)
          },
          '&.Mui-selected:hover': {
            background: alpha(colors.primary.lighter, 0.4)
          }
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.MuiButtonBase-root': {
            color: colors.secondary.main,

            '&:hover, &:active, &.active, &.Mui-selected': {
              color: colors.alpha.black[100],
              background: lighten(colors.primary.lighter, 0.5)
            }
          }
        }
      }
    },
    MuiAutocomplete: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        tag: {
          margin: 1
        },
        root: {
          '.MuiAutocomplete-inputRoot.MuiOutlinedInput-root .MuiAutocomplete-endAdornment':
            {
              right: 14
            }
        },
        clearIndicator: {
          background: colors.error.lighter,
          color: colors.error.main,
          marginRight: 8,

          '&:hover': {
            background: colors.error.lighter,
            color: colors.error.dark
          }
        },
        popupIndicator: {
          color: colors.alpha.black[50],

          '&:hover': {
            background: colors.primary.lighter,
            color: colors.primary.main
          }
        }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          minHeight: 44,
          '& .MuiIconButton-root': {
            padding: 8,
            borderRadius: 8
          },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
            {
              fontSize: '0.8125rem',
              color: colors.alpha.black[70],
              fontWeight: 500
            }
        },
        select: {
          fontSize: '0.8125rem',
          fontWeight: 500,
          '&:focus': {
            backgroundColor: 'transparent'
          }
        }
      }
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: colors.alpha.white[100],
          borderRadius: 0
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '0 !important',
          padding: '0 !important'
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        head: {
          background: 'transparent'
        },
        root: {
          transition: 'background-color .15s ease',
          '&.MuiTableRow-hover:hover, &:hover:not(.MuiTableRow-head)': {
            backgroundColor: brand.subtle
          },
          '&:last-child td': {
            borderBottom: 0
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: alpha(themeColors.black, 0.05),
          fontSize: '0.8125rem',
          lineHeight: 1.5,
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 16,
          paddingRight: 16,
          color: colors.alpha.black[80],
          fontVariantNumeric: 'tabular-nums',
          '@media (max-width: 600px)': {
            paddingTop: 9,
            paddingBottom: 9,
            paddingLeft: 12,
            paddingRight: 12,
            fontSize: '0.75rem'
          }
        },
        head: {
          textTransform: 'none',
          fontSize: '0.75rem',
          fontWeight: 500,
          letterSpacing: 0,
          paddingTop: 10,
          paddingBottom: 10,
          color: brand.muted,
          backgroundColor: 'transparent',
          borderBottom: `1px solid ${alpha(themeColors.black, 0.06)}`,
          whiteSpace: 'nowrap'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          lineHeight: 1.5,
          fontSize: 14
        },
        standardInfo: {
          color: colors.info.main
        },
        action: {
          color: colors.alpha.black[70]
        }
      }
    },
    MuiTimelineDot: {
      styleOverrides: {
        root: {
          margin: 0,
          zIndex: 5,
          position: 'absolute',
          top: '50%',
          marginTop: -6,
          left: -6
        },
        outlined: {
          backgroundColor: colors.alpha.white[100],
          boxShadow: '0 0 0 6px ' + colors.alpha.white[100]
        },
        outlinedPrimary: {
          backgroundColor: colors.alpha.white[100],
          boxShadow: '0 0 0 6px ' + colors.alpha.white[100]
        }
      }
    },
    MuiTimelineConnector: {
      styleOverrides: {
        root: {
          position: 'absolute',
          height: '100%',
          top: 0,
          borderRadius: 50,
          backgroundColor: colors.alpha.black[10]
        }
      }
    },
    MuiTimelineItem: {
      styleOverrides: {
        root: {
          minHeight: 0,
          padding: '8px 0',

          '&:before': {
            display: 'none'
          }
        },
        missingOppositeContent: {
          '&:before': {
            display: 'none'
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha(colors.alpha.black['100'], 0.95),
          padding: '8px 16px',
          fontSize: 13
        },
        arrow: {
          color: alpha(colors.alpha.black['100'], 0.95)
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 24,
          padding: 0,
          margin: 4,
          overflow: 'visible'
        },
        switchBase: {
          padding: 3,
          '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: colors.alpha.white[100],
            '& + .MuiSwitch-track': {
              backgroundColor: brand.chartLime,
              opacity: 1
            }
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.4
          }
        },
        thumb: {
          width: 18,
          height: 18,
          boxShadow: '0 1px 3px rgba(22, 25, 22, 0.25)',
          backgroundColor: colors.alpha.white[100]
        },
        track: {
          borderRadius: 12,
          backgroundColor: alpha(themeColors.black, 0.16),
          opacity: 1
        },
        sizeSmall: {
          width: 34,
          height: 20,
          '& .MuiSwitch-switchBase': {
            padding: 3,
            '&.Mui-checked': { transform: 'translateX(14px)' }
          },
          '& .MuiSwitch-thumb': { width: 14, height: 14 }
        }
      }
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          paddingTop: 20,
          paddingBottom: 20,
          background: colors.alpha.black[5]
        }
      }
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          '&.MuiStepIcon-completed': {
            color: colors.success.main
          }
        }
      }
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'div',
          h4: 'div',
          h5: 'div',
          h6: 'div',
          subtitle1: 'div',
          subtitle2: 'div',
          body1: 'div',
          body2: 'div'
        }
      },
      styleOverrides: {
        gutterBottom: {
          marginBottom: 4
        },
        paragraph: {
          fontSize: 17,
          lineHeight: 1.7
        }
      }
    }
  },
  shape: {
    borderRadius: 10
  },
  typography: {
    fontFamily:
      '"Plus Jakarta Sans", "Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: {
      fontFamily: '"Manrope", "Plus Jakarta Sans", sans-serif',
      fontWeight: 700,
      fontSize: 32,
      letterSpacing: '-0.03em'
    },
    h2: {
      fontFamily: '"Manrope", "Plus Jakarta Sans", sans-serif',
      fontWeight: 700,
      fontSize: 26,
      letterSpacing: '-0.025em'
    },
    h3: {
      fontFamily: '"Manrope", "Plus Jakarta Sans", sans-serif',
      fontWeight: 700,
      fontSize: 19,
      lineHeight: 1.3,
      letterSpacing: '-0.02em',
      color: colors.alpha.black[100]
    },
    h4: {
      fontFamily: '"Manrope", "Plus Jakarta Sans", sans-serif',
      fontWeight: 700,
      fontSize: 16,
      letterSpacing: '-0.02em'
    },
    h5: {
      fontWeight: 600,
      fontSize: 14,
      letterSpacing: '-0.01em'
    },
    h6: {
      fontWeight: 600,
      fontSize: 14,
      letterSpacing: '-0.01em'
    },
    body1: {
      fontSize: 14,
      lineHeight: 1.55
    },
    body2: {
      fontSize: 13,
      lineHeight: 1.5
    },
    button: {
      fontWeight: 600,
      textTransform: 'none'
    },
    caption: {
      fontSize: 12,
      letterSpacing: '0.02em',
      color: colors.alpha.black[50]
    },
    subtitle1: {
      fontSize: 14,
      color: colors.alpha.black[70]
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: 13,
      color: colors.alpha.black[70]
    },
    overline: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase'
    }
  },
  shadows: [
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none'
  ]
});
